
import { useState, useEffect } from 'react';

interface StickyNavProps {
  accentColor: string;
}

const sections = [
  { id: 'overview', label: 'Overview', icon: 'ri-information-line' },
  { id: 'businesses', label: 'Businesses', icon: 'ri-store-2-line' },
  { id: 'map', label: 'Map', icon: 'ri-map-2-line' },
  { id: 'contact', label: 'Contact', icon: 'ri-mail-line' },
];

export default function StickyNav({ accentColor }: StickyNavProps) {
  const [activeSection, setActiveSection] = useState('overview');
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY > 520);

      const offsets = sections.map((s) => {
        const el = document.getElementById(s.id);
        if (!el) return { id: s.id, top: Infinity };
        return { id: s.id, top: el.getBoundingClientRect().top };
      });

      const current = offsets.reduce((closest, section) => {
        if (section.top <= 120 && section.top > closest.top) return section;
        return closest;
      }, { id: 'overview', top: -Infinity });

      setActiveSection(current.id);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const offset = 80;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  if (!isVisible) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-sm shadow-sm border-b border-gray-100 animate-fadeIn">
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-14">
        <div className="flex items-center gap-6">
          {sections.map((s) => (
            <button
              key={s.id}
              onClick={() => scrollTo(s.id)}
              className={`flex items-center gap-1.5 text-sm font-medium transition-all cursor-pointer whitespace-nowrap relative py-4 ${
                activeSection === s.id
                  ? 'text-gray-900'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <i className={`${s.icon} w-4 h-4 flex items-center justify-center`}></i>
              {s.label}
              {activeSection === s.id && (
                <span
                  className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full"
                  style={{ backgroundColor: accentColor }}
                ></span>
              )}
            </button>
          ))}
        </div>

        <a
          href="#contact"
          onClick={(e) => {
            e.preventDefault();
            scrollTo('contact');
          }}
          className="px-4 py-2 bg-teal-600 text-white text-xs font-semibold rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
        >
          Get in Touch
        </a>
      </div>
    </div>
  );
}
